<script setup lang="ts">
// One legal document, word for word as the RomBa app shows it. The HTML is
// generated from the app repository by scripts/sync-legal.mjs into
// app/legal/generated — one file per document and language, and only the
// current language's file is loaded.
type LegalDoc = 'terms' | 'privacy' | 'agreement'
type LegalFile = { default: { title: string; html: string } }

const props = defineProps<{ doc: LegalDoc }>()

const { t, locale } = useI18n()
const config = useRuntimeConfig()
const route  = useRoute()

useReveal()

const files = import.meta.glob<LegalFile>('../legal/generated/*.*.json')

const { data } = await useAsyncData(
  () => `legal-${props.doc}-${locale.value}`,
  async () => {
    const load = files[`../legal/generated/${props.doc}.${locale.value}.json`]
      ?? files[`../legal/generated/${props.doc}.en.json`]
    return load ? (await load()).default : null
  },
)

const siteUrl = config.public.siteUrl as string
const canonical = computed(() => {
  const path = route.path.endsWith('/') ? route.path : `${route.path}/`
  return `${siteUrl}${path}`
})

useHead(() => ({
  title: t(`legal.${props.doc}.meta.title`),
  meta: [
    { name: 'description',         content: t(`legal.${props.doc}.meta.description`) },
    { property: 'og:title',        content: t(`legal.${props.doc}.meta.title`) },
    { property: 'og:description',  content: t(`legal.${props.doc}.meta.description`) },
    { property: 'og:url',          content: canonical.value },
    { name: 'twitter:title',       content: t(`legal.${props.doc}.meta.title`) },
    { name: 'twitter:description', content: t(`legal.${props.doc}.meta.description`) },
    { name: 'robots',              content: 'index,follow' },
  ],
  link: [{ rel: 'canonical', href: canonical.value }],
}))
</script>

<template>
  <div>
    <SiteNav />

    <PageHero
      :eyebrow="t('legal.eyebrow')"
      :title="t(`legal.${doc}.title`)"
      :lead="t(`legal.${doc}.lead`)"
    />

    <section class="rod-section pt-0">
      <div class="rod-container max-w-3xl">
        <p class="text-sm text-[var(--color-text-dim)] mb-8">
          {{ t('legal.sameAsApp') }}
        </p>
        <!-- Trusted, generated from the app's reviewed sources. -->
        <article v-if="data" class="legal-doc" v-html="data.html" />
      </div>
    </section>

    <SiteFooter />
  </div>
</template>

<style scoped>
.legal-doc { color: var(--color-text-muted); line-height: 1.7; overflow-wrap: anywhere; }
.legal-doc :deep(h2) {
  font-family: var(--font-display);
  color: var(--color-text);
  font-size: 1.5rem;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 2.5rem 0 1rem;
}
.legal-doc :deep(h3),
.legal-doc :deep(h4) {
  font-family: var(--font-display);
  color: var(--color-text);
  font-size: 1.15rem;
  font-weight: 700;
  margin: 1.75rem 0 0.75rem;
}
/* Linked sections (#section-7) land below the sticky menu, not under it. */
.legal-doc :deep(h2),
.legal-doc :deep(h3)     { scroll-margin-top: 6rem; }
.legal-doc :deep(p)      { margin-bottom: 1rem; }
.legal-doc :deep(strong) { color: var(--color-text); }
.legal-doc :deep(a)      { color: var(--color-gold-400); text-decoration: underline; }
.legal-doc :deep(code)   { font-size: 0.9em; }
.legal-doc :deep(hr)     { border: 0; border-top: 1px solid rgb(255 255 255 / 0.08); margin: 2rem 0; }
.legal-doc :deep(ul)     { list-style: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
.legal-doc :deep(ol)     { list-style: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
.legal-doc :deep(li)     { margin-bottom: 0.5rem; }
</style>
